import type { Project, ServiceContract, TimeLog, WorkspaceOffice } from '../../types';
import { clampGeofenceRadiusFt, DEFAULT_GEOFENCE_FT, haversineDistanceFt } from '../../utils/geomath';

/**
 * Pure Time Clock helpers — no React, so they can be unit-tested and shared by
 * the personal panel and the team feed.
 */

/** Why a location couldn't be resolved — lets callers show a precise message. */
export type GeoError = 'denied' | 'unavailable' | 'timeout' | 'unsupported' | 'insecure';

export interface GeoResult {
    location: { lat: number; lng: number } | null;
    /** Reason location is missing; null when `location` resolved successfully. */
    error: GeoError | null;
}

/**
 * Resolve the device location as precisely as possible.
 *
 * A GPS fix CONVERGES: the first reading after the receiver wakes is coarse
 * (often ±50-100 m) and tightens over a few seconds as more satellites lock.
 * So instead of taking that first reading, we WATCH for a short window and
 * keep the most accurate fix, returning early once it's good enough. For
 * outdoor work — construction sites — this reliably lands within a few metres.
 *
 * `enableHighAccuracy: true` is the GPS request; `maximumAge: 0` forbids a
 * cached position. There is intentionally no low-accuracy network fallback —
 * a coarse WiFi/IP fix is worse than briefly waiting for real GPS.
 */
export function getGeoLocation(): Promise<GeoResult> {
    return new Promise((resolve) => {
        if (!navigator.geolocation) {
            resolve({ location: null, error: 'unsupported' });
            return;
        }
        // Geolocation only works in a secure context (HTTPS or localhost). Over
        // plain HTTP — e.g. reaching a dev server by LAN IP — the browser
        // rejects every request as "denied" regardless of the granted permission.
        if (typeof window !== 'undefined' && window.isSecureContext === false) {
            resolve({ location: null, error: 'insecure' });
            return;
        }

        const GOOD_ACCURACY_M = 15;   // stop early once a fix is at least this tight
        const WATCH_WINDOW_MS = 8000; // ...otherwise stop watching after this long

        let best: GeolocationPosition | null = null;
        let settled = false;
        let watchId: number | null = null;
        let timer: ReturnType<typeof setTimeout> | null = null;

        const finish = (error: GeoError | null) => {
            if (settled) return;
            settled = true;
            if (watchId !== null) navigator.geolocation.clearWatch(watchId);
            if (timer !== null) clearTimeout(timer);
            resolve(best
                ? { location: { lat: best.coords.latitude, lng: best.coords.longitude }, error: null }
                : { location: null, error: error ?? 'timeout' });
        };

        watchId = navigator.geolocation.watchPosition(
            (pos) => {
                // Keep the tightest fix seen so far; bail early once it's precise enough.
                if (!best || pos.coords.accuracy < best.coords.accuracy) best = pos;
                if (best && best.coords.accuracy <= GOOD_ACCURACY_M) finish(null);
            },
            (err) => {
                // A denial is final. Any other error: keep whatever fix we already have.
                if (err.code === 1) { finish('denied'); return; }
                if (!best) finish(err.code === 3 ? 'timeout' : 'unavailable');
            },
            { enableHighAccuracy: true, maximumAge: 0, timeout: WATCH_WINDOW_MS }
        );

        // End of the watch window — resolve with the best fix gathered so far.
        timer = setTimeout(() => finish('timeout'), WATCH_WINDOW_MS);
    });
}

/** Seconds → "HH:MM:SS". */
export function formatDuration(totalSeconds: number): string {
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/** Live NET worked seconds for an active shift: elapsed since clock-in minus the
 *  lunch break (completed or still in progress). Shared by the personal timer
 *  and the team feed so both report the same net number. */
export function liveWorkedSeconds(log: TimeLog, now: Date): number {
    let elapsed = Math.max(0, Math.floor((now.getTime() - new Date(log.clockIn).getTime()) / 1000));
    if (log.lunchStart && log.lunchEnd) {
        elapsed -= Math.max(0, Math.floor((new Date(log.lunchEnd).getTime() - new Date(log.lunchStart).getTime()) / 1000));
    } else if (log.lunchStart && !log.lunchEnd) {
        elapsed -= Math.max(0, Math.floor((now.getTime() - new Date(log.lunchStart).getTime()) / 1000));
    }
    return Math.max(0, elapsed);
}

/**
 * Encoded values used by the target picker. Keeps a single <select> while still
 * tracking whether the user picked a one-off project or a recurring route.
 *   - ''           → SIN destino. La opción vacía de siempre, la que se ofrece
 *                    mientras el taller no tenga dirección configurada.
 *   - 'office'     → el taller del espacio de trabajo (workspace_offices)
 *   - 'proj:<id>'  → projects.id
 *   - 'svc:<id>'   → service_contracts.id
 */
export type ClockTargetValue = '' | 'office' | `proj:${string}` | `svc:${string}`;

/** El destino elegido, ya resuelto contra los datos. Como mucho uno de los tres
 *  viene con algo; los tres en nulo son las horas generales de siempre. */
export interface ClockSelection {
    project: Project | null;
    contract: ServiceContract | null;
    office: WorkspaceOffice | null;
}

const SIN_DESTINO: ClockSelection = { project: null, contract: null, office: null };

/**
 * `office` llega como PARÁMETRO y no se busca en ninguna lista: hay como mucho
 * una por taller y quien llama ya la tiene del hook.
 *
 * Ojo con el caso de hoy en producción: `workspace_offices` está VACÍA en todos
 * los talleres, así que `'office'` se resuelve a nulo y la selección queda
 * exactamente igual que `''` — sin destino, las horas generales de siempre. Es
 * a propósito: el camino sin oficina configurada tiene que seguir funcionando
 * tal cual, que es lo que hoy usa el 100% de la gente.
 */
export const decodeTarget = (
    value: ClockTargetValue,
    projects: Project[],
    contracts: ServiceContract[],
    office: WorkspaceOffice | null = null,
): ClockSelection => {
    if (!value) return SIN_DESTINO;
    if (value === 'office') return { project: null, contract: null, office };
    if (value.startsWith('proj:')) {
        const id = value.slice(5);
        return { project: projects.find(p => p.id === id) || null, contract: null, office: null };
    }
    if (value.startsWith('svc:')) {
        const id = value.slice(4);
        return { project: null, contract: contracts.find(c => c.id === id) || null, office: null };
    }
    return SIN_DESTINO;
};

/** Contra qué se mide un fichaje: el punto y el radio, en pies. */
export interface ClockFence {
    /** `null` = ese destino no tiene coordenadas y no se puede medir nada. */
    coords: { lat: number; lng: number } | null;
    radiusFt: number;
}

/**
 * La geocerca de un destino.
 *
 * Esta cuenta estaba escrita TRES veces dentro del panel del reloj —al fichar
 * entrada, al fichar salida y en la línea que anuncia «Geofence activo»— y las
 * tres no decían lo mismo: dos respaldaban el radio con `?? 300` y la otra con
 * `|| 300`. Con un radio guardado en 0 eso son dos comportamientos opuestos
 * (todo fuera de obra contra el respaldo entero), y quien leía la línea de la
 * pantalla no veía el número con el que de verdad se le iba a medir.
 *
 * El radio pasa por `clampGeofenceRadiusFt`, que es el único sitio donde viven
 * el respaldo y las cotas — las mismas que el CHECK de la base.
 */
export const fenceFor = (selection: ClockSelection): ClockFence => {
    if (selection.project) {
        return {
            coords: selection.project.siteCoordinates ?? null,
            radiusFt: clampGeofenceRadiusFt(selection.project.geofenceRadiusFt),
        };
    }
    if (selection.contract) {
        // Un contrato no guarda radio propio: se mide con el del taller.
        return { coords: selection.contract.location ?? null, radiusFt: DEFAULT_GEOFENCE_FT };
    }
    if (selection.office) {
        return {
            coords: selection.office.siteCoordinates ?? null,
            radiusFt: clampGeofenceRadiusFt(selection.office.geofenceRadiusFt),
        };
    }
    return { coords: null, radiusFt: DEFAULT_GEOFENCE_FT };
};

export interface FenceVerdict {
    isOffSite: boolean;
    /** `undefined` cuando no había con qué medir — que NO es lo mismo que 0. */
    distanceFt?: number;
}

/**
 * ¿Este fichaje cae dentro de la geocerca?
 *
 * Sin ubicación o sin coordenadas del destino la respuesta es «dentro»: la
 * geocerca AVISA, nunca impide fichar, y marcar fuera de obra a quien no dio
 * permiso de GPS sería acusar a alguien por una preferencia del teléfono.
 */
export const evaluateFence = (
    location: { lat: number; lng: number } | null | undefined,
    fence: ClockFence,
): FenceVerdict => {
    if (!location || !fence.coords) return { isOffSite: false };
    const distanceFt = Math.round(haversineDistanceFt(location, fence.coords));
    return { isOffSite: distanceFt > fence.radiusFt, distanceFt };
};

/** Downscale an image File to a JPEG no wider than maxWidth, so clock-in
 *  verification photos stay small (storage cost + mobile data). Returns the
 *  original file unchanged if anything in the pipeline fails. */
export async function compressImage(file: File, maxWidth = 800, quality = 0.7): Promise<File> {
    try {
        const bitmap = await createImageBitmap(file);
        const scale = Math.min(1, maxWidth / bitmap.width);
        const w = Math.round(bitmap.width * scale);
        const h = Math.round(bitmap.height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) return file;
        ctx.drawImage(bitmap, 0, 0, w, h);
        const blob = await new Promise<Blob | null>(res => canvas.toBlob(res, 'image/jpeg', quality));
        bitmap.close();
        if (!blob) return file;
        return new File([blob], `clockin-${Date.now()}.jpg`, { type: 'image/jpeg' });
    } catch (err) {
        console.warn('[TimeClock] image compression failed, using original:', err);
        return file;
    }
}
